import { Amm } from '../amm/types';
import { Route, RouteStep, TokenRouteSegments, Segment } from './types';

function addSegment(
  inMint: string,
  outMint: string,
  amm: Amm,
  tokenRouteSegments: TokenRouteSegments
): void {
  let segments = tokenRouteSegments.get(inMint);

  if (!segments) {
    segments = new Map([[outMint, []]]);
    tokenRouteSegments.set(inMint, segments);
  }

  let marketMetas = segments.get(outMint);

  if (!marketMetas) {
    marketMetas = [];
    segments.set(outMint, marketMetas);
  }

  marketMetas.push({ inputToken: inMint, outputToken: outMint, amm });
}

function getTwoPermutations<T>(array: T[]) {
  return array.reduce<T[][]>((acc, item) => {
    array.forEach((otherItem) => {
      if (item !== otherItem) {
        acc.push([item, otherItem]);
      }
    });
    return acc;
  }, []);
}

function getTokenRouteSegments(amms: Amm[]): TokenRouteSegments {
  const tokenRouteSegments: TokenRouteSegments = new Map();

  amms.forEach((amm: Amm) => {
    const reserveTokenMints = amm.reserveTokenMints;
    const reserveTokenMintPermutations = getTwoPermutations(reserveTokenMints);

    reserveTokenMintPermutations.forEach(
      ([firstReserveMint, secondReserveMint]) => {
        addSegment(
          firstReserveMint,
          secondReserveMint,
          amm,
          tokenRouteSegments
        );
      }
    );
  });

  return tokenRouteSegments;
}

function computeRoutes({
  inputMint,
  outputMint,
  tokenRouteSegments,
  intermediateTokens,
}: {
  inputMint: string;
  outputMint: string;
  tokenRouteSegments: TokenRouteSegments;
  intermediateTokens: string[];
}): Route[] {
  const routes = [];
  const firstSegment: Segment = tokenRouteSegments.get(inputMint) || new Map();
  const simpleRoutes: RouteStep[] = firstSegment.get(outputMint) || []; // Direct trade

  simpleRoutes.forEach((simpleRoute) => {
    // dont do direct decimal saber
    routes.push([simpleRoute]);
  });

  const secondSegment: Segment =
    tokenRouteSegments.get(outputMint) || new Map();

  for (const [mint, marketMetas] of firstSegment.entries()) {
    if (intermediateTokens) {
      // if it doesnt include in the intermediateTokens, skip it
      if (!intermediateTokens.includes(mint)) {
        // eslint-disable-next-line no-continue
        continue;
      }
    }

    const intersectionMarketMetas = secondSegment.get(mint) || [];

    for (const marketMeta of marketMetas) {
      for (const intersectionMarketMeta of intersectionMarketMetas) {
        // we need to reverse here, because found intersectionMarketMetas using mint as inputToken
        const secondStepWithReversedTokens: RouteStep = {
          amm: intersectionMarketMeta.amm,
          inputToken: intersectionMarketMeta.outputToken,
          outputToken: intersectionMarketMeta.inputToken,
        };

        routes.push([marketMeta, secondStepWithReversedTokens]);
      }
    }
  }

  return routes;
}

export { computeRoutes, getTokenRouteSegments };
